import type { ReactNode } from 'react';

import { ArrowSquareOut } from '@phosphor-icons/react';
import { Button } from '@clay-run/glaze/button';
import { Card } from '@clay-run/glaze/card';
import { Icon } from '@clay-run/glaze/icon';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { Text } from '@clay-run/glaze/text';

import { playDisplayName, playStatusBadgeStatus, playStatusLabel } from '../plays-logic';
import type { UsageCard } from '../usage-sidebar-logic';

export interface UsagePlayCardsProps {
  cards: readonly UsageCard[];
  currentIndex?: number;
  playbookNameById: ReadonlyMap<string, string>;
  onNavigate: (card: UsageCard) => void;
}

/**
 * One labeled row inside a card: the value on top (`Text size="sm"`), the
 * caption label underneath (`Text size="xs" variant="tertiary"`) — carried
 * over verbatim from the pre-U6 `UsagePlacesList.tsx#PlaceRow`.
 */
function CardRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex flex-wrap items-center gap-2">{children}</div>
      <Text size="xs" variant="tertiary">
        {label}
      </Text>
    </div>
  );
}

/**
 * QA round 8, U7 (2026-09-22) — one card per {@link UsageCard}, which is now
 * one RUNG x PLAY pair (a `'playbook'` card for a rung whose playbook has no
 * plays yet). The card is TITLED by its segment name, because that is the
 * thing a rep recognises and the thing "Update N segments" counts:
 *
 * ```
 * [Card]
 *   {segmentName}                                   <- title row, always
 *   [You're editing this one]                       <- index === currentIndex only
 *   {playDisplayName(...)} · {campaignName}          Play        <- 'play' cards
 *   [Draft|Locked — already sent]                    <- 'play' cards (R13)
 *   [No plays yet]                                   <- 'playbook' cards
 *   {playbookName}                                   Playbook
 *   [↗ Open in new tab]                              <- every card EXCEPT the origin
 * ```
 *
 * The control is "Open in new tab", not "Go to this play": since U7 the
 * sidebar `window.open`s the card's URL, so the page underneath (and the
 * rep's unsaved typing on it) never moves. Its `aria-label` names the
 * segment so a screen reader — and the accessibility tree browser QA reads —
 * can tell several otherwise-identical controls apart.
 *
 * Renders `null` for an empty `cards` — callers never show this list for
 * zero cards; the sidebar's own zero-usage copy branch covers that case.
 */
export function UsagePlayCards({ cards, currentIndex, playbookNameById, onNavigate }: UsagePlayCardsProps) {
  if (cards.length === 0) return null;

  return (
    <ul className="flex flex-col gap-3">
      {cards.map((card, index) => {
        const isOrigin = index === currentIndex;
        // `placeKey` is unique per rung; a play id is unique within a rung.
        const key = card.kind === 'play' ? `${card.placeKey}|play:${card.playId}` : `${card.placeKey}|playbook`;

        return (
          <li key={key}>
            <Card padded className={isOrigin ? 'border-2 border-border-action' : undefined}>
              <div className="flex flex-col gap-3">
                <Text size="sm" weight="semibold">
                  {card.segmentName}
                </Text>
                {isOrigin && <StatusBadge status="action">You&apos;re editing this one</StatusBadge>}
                {card.kind === 'play' ? (
                  <>
                    <CardRow label="Play">
                      <Text size="sm">
                        {playDisplayName({ name: card.playName, playbookId: card.playbookId }, playbookNameById)}
                        {' · '}
                        {card.campaignName ?? 'Campaign not in the mirror'}
                      </Text>
                    </CardRow>
                    <StatusBadge status={playStatusBadgeStatus(card.status)}>
                      {/* Plan 2026-09-28-006 U7 (rulebook R13) — a launched
                          play is locked: ONE badge (not "Sent" plus a second
                          "Locked" tag) reads "Locked — already sent",
                          keeping `playStatusBadgeStatus`'s colour so a
                          launched card still reads success-green. */}
                      {card.status === 'launched' ? 'Locked — already sent' : playStatusLabel(card.status)}
                    </StatusBadge>
                  </>
                ) : (
                  <StatusBadge status="neutral">No plays yet</StatusBadge>
                )}
                <CardRow label="Playbook">
                  <Text size="sm">{card.playbookName}</Text>
                </CardRow>
                {!isOrigin && (
                  <div>
                    <Button
                      variant="inline-text"
                      size="xs"
                      aria-label={`Open ${card.segmentName} in a new tab`}
                      onClick={() => onNavigate(card)}
                    >
                      <Icon type={ArrowSquareOut} />
                      Open in new tab
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          </li>
        );
      })}
    </ul>
  );
}
